import {
  PAYROLL_ROSTER_FIELD_TYPE,
  type PayrollRosterField,
  type PayrollRosterMember,
} from "@/payroll/roster/types";
import { formatRosterValue } from "@/payroll/roster/utils";
import { ROSTER_NAME_COLUMN_LABEL } from "./constants";

const APPROXIMATE_CHARACTER_WIDTH = 8;
const CELL_PADDING_AND_SORT_ICON_WIDTH = 48;

export const TEAM_COLUMN_WIDTH = 200;
// The cell stacks the name over the ServiceTitan ID and can also show a status
// badge. Keep enough room to scan it without taking width from roster fields.
export const NAME_COLUMN_MIN_WIDTH = 224;
export const NAME_COLUMN_MAX_WIDTH = 304;
export const FIELD_COLUMN_MIN_WIDTH = 176;
export const FIELD_COLUMN_MAX_WIDTH = 320;
export const BOOLEAN_FIELD_COLUMN_MAX_WIDTH = 224;

export const estimateRosterColumnWidth = (
  values: readonly string[],
  minimum: number,
  maximum: number,
) => {
  const longestValueLength = values.reduce(
    (longest, value) => Math.max(longest, Array.from(value).length),
    0,
  );
  const estimatedWidth =
    longestValueLength * APPROXIMATE_CHARACTER_WIDTH +
    CELL_PADDING_AND_SORT_ICON_WIDTH;

  return Math.min(maximum, Math.max(minimum, estimatedWidth));
};

export const getRosterColumnWidths = (
  members: readonly PayrollRosterMember[],
  fields: readonly PayrollRosterField[],
) => {
  const name = estimateRosterColumnWidth(
    [
      ROSTER_NAME_COLUMN_LABEL,
      ...members.map((member) => member.display_name || "-"),
    ],
    NAME_COLUMN_MIN_WIDTH,
    NAME_COLUMN_MAX_WIDTH,
  );
  const fieldWidths = new Map(
    fields.map((field) => {
      const maximumWidth =
        field.field_type === PAYROLL_ROSTER_FIELD_TYPE.boolean
          ? BOOLEAN_FIELD_COLUMN_MAX_WIDTH
          : FIELD_COLUMN_MAX_WIDTH;

      return [
        field.key,
        estimateRosterColumnWidth(
          [
            field.label,
            ...members.map((member) =>
              formatRosterValue(member.field_values[field.key], field),
            ),
          ],
          FIELD_COLUMN_MIN_WIDTH,
          maximumWidth,
        ),
      ] as const;
    }),
  );

  return {
    name,
    fields: fieldWidths,
    tableMinWidth:
      name +
      Array.from(fieldWidths.values()).reduce((sum, width) => sum + width, 0),
  };
};
